/**
 * Scan Limits Utility
 * 
 * Handles scan counting and limit checking based on subscription tier
 */

import { SubscriptionTier, getScansPerWeek, hasUnlimitedScans } from '../types/subscription';
import { listUserScans, ScanRecord } from '../services/scanService';

/**
 * Get the start of the current week (Monday) in user's local timezone
 */
export function getWeekStart(date: Date = new Date()): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Count scans in the current week
 */
export async function countScansThisWeek(
  session: any,
  userId: string
): Promise<number> {
  try {
    const scans = await listUserScans(session, userId);
    const weekStart = getWeekStart();
    
    const scansThisWeek = scans.items.filter((scan: ScanRecord) => {
      if (!scan.timestamp && !scan.createdAt) return false;
      const scanDate = new Date(scan.timestamp || scan.createdAt || '');
      return scanDate >= weekStart;
    });
    
    return scansThisWeek.length;
  } catch (error) {
    console.error('Error counting scans:', error);
    return 0;
  }
}

/**
 * Check if user can perform a scan
 */
export async function canPerformScan(
  tier: SubscriptionTier,
  session: any,
  userId: string
): Promise<{ allowed: boolean; reason?: string; scansUsed?: number; scansAllowed?: number | 'unlimited' }> {
  if (hasUnlimitedScans(tier)) {
    return { allowed: true, scansAllowed: 'unlimited' };
  }

  const scansAllowed = getScansPerWeek(tier);
  if (scansAllowed === 'unlimited') {
    return { allowed: true, scansAllowed: 'unlimited' };
  }

  const scansUsed = await countScansThisWeek(session, userId);
  
  if (scansUsed >= scansAllowed) {
    return {
      allowed: false,
      reason: `You've reached your weekly limit of ${scansAllowed} scans. Upgrade to unlock unlimited scans.`,
      scansUsed,
      scansAllowed,
    };
  }

  return {
    allowed: true,
    scansUsed,
    scansAllowed,
  };
}
